import { useKindeAuth } from '@kinde-oss/kinde-auth-react';

import { Box, Divider, Stack, Typography } from '@mui/material';

import type { CommentResponse } from '@/designs-dev/backend/client';
import {
  isResolved,
  isUnread,
  repliesTo,
  selectThread,
  threadsOf,
  useComments,
} from '@/designs-dev/comments/store';
import { Message } from '@/designs-dev/comments/Thread';

// Every thread on the page, numbered to match their pins: the open ones, then
// the resolved ones set apart below. Opened from its own button, never by
// comment mode itself: reading and replying happen in the bubble beside a pin,
// and this is for finding one. Sits in the drill panel's dock on a prototype
// (InspectPanel) and beside the canvas on a flows or spec page
// (CanvasComments).
const CommentsPanel = ({
  placedOf = (anchors) => new Set(anchors),
  onOpen,
}: {
  // Which of these anchors can be found on the page right now; the rest are
  // marked as not on screen. Everything, unless the host says otherwise.
  placedOf?: (anchors: string[]) => Set<string>;
  // A thread was opened from the list, for the host to bring its pin into view.
  onOpen?: (thread: CommentResponse) => void;
}) => {
  const { user, isAuthenticated } = useKindeAuth();
  const store = useComments();
  const { comments, loading, error, selected } = store;

  // Numbered across every thread, resolved or not, so a pin's number doesn't
  // change when another thread is resolved.
  const threads = threadsOf(comments).map((t, i) => ({ t, n: i + 1 }));
  const open = threads.filter(({ t }) => !isResolved(comments, t.id));
  const resolved = threads.filter(({ t }) => isResolved(comments, t.id));
  const placed = placedOf(threads.map(({ t }) => t.anchor));

  const note = (text: string) => (
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {text}
    </Typography>
  );

  const card = ({ t, n }: { t: CommentResponse; n: number }, done: boolean) => {
    const isOpen = selected === t.id;
    const replies = repliesTo(comments, t.id).length;
    const unread = !done && isUnread(store, t, user?.id);
    return (
      <Stack
        key={t.id}
        spacing={1}
        onClick={() => {
          selectThread(isOpen ? null : t.id);
          if (!isOpen) onOpen?.(t);
        }}
        sx={{
          p: 1.5,
          border: 1,
          borderColor: isOpen ? 'primary.main' : 'custom.neutral.200',
          borderRadius: '8px',
          cursor: 'pointer',
          // Resolved threads stay findable but read as finished with.
          opacity: done ? 0.6 : 1,
        }}
      >
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant='caption' sx={{ fontWeight: 600 }}>
            #{n}
          </Typography>
          {unread && (
            <Box
              aria-label='New'
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'custom.coral.600',
              }}
            />
          )}
          {!placed.has(t.anchor) && (
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Not on screen
            </Typography>
          )}
        </Stack>
        <Message
          comment={t}
          mine={t.author.id === user?.id}
          label='Delete thread'
        />
        {replies > 0 && (
          <Typography variant='caption' sx={{ color: 'text.secondary', pl: 4 }}>
            {replies === 1 ? '1 reply' : `${replies} replies`}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Typography variant='h5' sx={{ mb: 2, flexShrink: 0 }}>
        Comments
      </Typography>
      <Divider sx={{ mx: { xs: -2, md: -3 }, mb: 2 }} />

      {!isAuthenticated ? (
        note('Sign in to see and leave comments.')
      ) : error ? (
        note("Couldn't load comments.")
      ) : loading ? null : threads.length === 0 ? (
        note('No comments yet.')
      ) : (
        <Stack spacing={1.5}>
          {open.map((x) => card(x, false))}
          {resolved.length > 0 && (
            <>
              <Typography
                variant='overline'
                sx={{ color: 'text.secondary', pt: open.length ? 1.5 : 0 }}
              >
                Resolved
              </Typography>
              {resolved.map((x) => card(x, true))}
            </>
          )}
        </Stack>
      )}
    </>
  );
};

export default CommentsPanel;
